import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, useMediaQuery } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useHistory } from "react-router-dom";
import BackgroundImage from "../elements/BackgroundImage";
import OrangeHighlightButton from "../elements/OrangeHighlightButton";
import theme from "../elements/Theme";
import RedButton from "./RedButton";

interface HeroSectionProps {
  mainTitle: ReactNode;
  secondaryTitle: ReactNode | string;
  buttonText?: string;
  buttonText2?: string;
  buttonOnClickPath?: string;
  buttonOnClickPath2?: string;
  backgroundImageUrl?: string;
  altImage?: string;
  isHomePage?: boolean;
  isApproachPage?: boolean;
  isPricingPage?: boolean;
  isNurseryPage?: boolean;
  formRef?: React.RefObject<HTMLFormElement | null>;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  mainTitle,
  secondaryTitle,
  buttonText,
  buttonText2,
  buttonOnClickPath,
  buttonOnClickPath2,
  backgroundImageUrl,
  altImage,
  isHomePage,
  isApproachPage,
  isPricingPage,
  isNurseryPage,
  formRef,
}) => {
  let history = useHistory();

  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";

  const isSmallMobile = useMediaQuery(theme.breakpoints.down(390));
  const isShortScreen = useMediaQuery("(max-height: 760px)");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));

  const handleClick = (route?: string | null) => {
    if (route) {
      const path =
        currentLanguage === "en" ? `/${route}` : `/${currentLanguage}/${route}`;
      history.push(path);
    }
    if (formRef) {
      formRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const withLineBreaks = (text: string) => {
    if (text.includes("\n")) {
      return text.split("\n").map((line, index) => (
        <span key={index}>
          {line}
          {index < text.split("\n").length - 1 && (
            <>
              <br />
              <br />
            </>
          )}
        </span>
      ));
    }
    return text;
  };

  const renderContent = () => (
    <Box
      sx={{
        mx: "auto",
        display: isApproachPage || isNurseryPage ? "flex" : "flex",
        flexDirection:
          isApproachPage || isNurseryPage
            ? isLaptop
              ? "column"
              : "row"
            : "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: isApproachPage || isNurseryPage ? "left" : "center",
        width: "100%",
        height:
          isApproachPage || isNurseryPage
            ? "100%"
            : isPricingPage && isMobile
            ? "74vh"
            : "100vh",
        gap: isApproachPage || isNurseryPage ? 4 : 0,
        backgroundColor:
          isApproachPage || isNurseryPage
            ? theme.palette.text.secondary
            : "transparent",
      }}
    >
      {(isApproachPage || isNurseryPage) && (
        <Box
          sx={{
            flex: isLaptop ? 12 : 4,
            height: "100%",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={backgroundImageUrl}
            alt={altImage}
            sx={{
              width: "100%",
              height: "auto",
              objectFit: "cover",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.35)",
              zIndex: 1,
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          flex:
            isApproachPage || isNurseryPage ? (isLaptop ? 12 : 6) : "initial",
          display: "flex",
          flexDirection: "column",
          alignItems:
            isApproachPage || isNurseryPage
              ? isLaptop
                ? "center"
                : "flex-start"
              : "center",
          justifyContent: "center",
          zIndex: 2,
          px: isApproachPage || isNurseryPage ? (isLaptop ? 2 : 0) : 0,
        }}
      >
        <Typography
          variant={"h1"}
          gutterBottom
          sx={{
            color: theme.palette.primary.main,
            lineHeight: 1.05,
            fontSize:
              isSmallMobile && isShortScreen
                ? "28px !important"
                : isApproachPage || isNurseryPage
                ? "48px !important"
                : formRef
                ? "68px !important"
                : "40px",
          }}
        >
          {mainTitle}
        </Typography>
        <Typography
          variant={"h4"}
          color={"white"}
          sx={{
            fontSize:
              isSmallMobile && isShortScreen
                ? "1rem !important"
                : isApproachPage || isNurseryPage
                ? "1.15rem !important"
                : "1.2853rem",
            paddingRight:
              isApproachPage || isNurseryPage ? (isLaptop ? 0 : "20%") : 0,
          }}
        >
          {(isApproachPage || isNurseryPage) &&
          typeof secondaryTitle === "string"
            ? withLineBreaks(secondaryTitle)
            : secondaryTitle}
        </Typography>
        {buttonText && buttonOnClickPath && <Box sx={{ height: "10vh" }} />}
        {buttonText && buttonOnClickPath && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pb: (isApproachPage || isNurseryPage) && isLaptop ? 4 : 0,
            }}
          >
            <OrangeHighlightButton
              onClick={() => handleClick(buttonOnClickPath)}
            >
              <Typography variant="h5">{buttonText}</Typography>
            </OrangeHighlightButton>
            {isHomePage && buttonText2 && buttonOnClickPath2 && (
              <OrangeHighlightButton
                sx={{ ml: 4 }}
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                      event: "enroll_button_click",
                      language: currentLanguage,
                    });
                  }
                  handleClick(buttonOnClickPath2);
                }}
              >
                <Typography variant="h5">{buttonText2}</Typography>
              </OrangeHighlightButton>
            )}
          </Box>
        )}
        {formRef && (
          <>
            <br />
            <RedButton onClick={() => handleClick(null)}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="800"
                  color={theme.palette.primary.main}
                  mr={1}
                >
                  {buttonText}
                </Typography>
                <ArrowDownwardIcon sx={{ fontSize: 20 }} />
              </Box>
            </RedButton>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: isPricingPage && isMobile ? "#4C8AFF" : "transparent",
      }}
    >
      {!(isApproachPage || isNurseryPage) ? (
        <BackgroundImage
          imageUrl={backgroundImageUrl}
          altImage={altImage}
          isPricingPage={isPricingPage}
        >
          {renderContent()}
        </BackgroundImage>
      ) : (
        renderContent()
      )}
    </Box>
  );
};

export default HeroSection;
